/**
 * Cross-domain UTM capture.
 *
 * castready.org (marketing) deep-links into hub.castready.org with `utm_*`
 * params. We persist them once at app boot so:
 *   - the signup pipeline can attribute the lead (passed to complete-signup)
 *   - analytics events fire with the first-touch attribution
 *
 * 24h TTL prevents stale attribution from previous sessions polluting later
 * conversions.
 */

const KEY = "castready_utms";
const TTL_MS = 24 * 60 * 60 * 1000;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
type UtmKey = (typeof UTM_KEYS)[number];

export type Utms = Partial<Record<UtmKey, string>> & { capturedAt?: number };

export function captureUtmsFromUrl(): Utms | null {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const found: Utms = {};
    for (const k of UTM_KEYS) {
      const v = url.searchParams.get(k);
      if (v) found[k] = v;
    }
    if (Object.keys(found).length === 0) return readStoredUtms();
    found.capturedAt = Date.now();
    sessionStorage.setItem(KEY, JSON.stringify(found));
    return found;
  } catch {
    return null;
  }
}

export function readStoredUtms(): Utms | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Utms;
    if (!parsed.capturedAt || Date.now() - parsed.capturedAt > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredUtms(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
